import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import { MY_PGA_APPLE_APP_STORE, MY_PGA_GOOGLE_PLAY_STORE } from 'env'
import CheckIcon from 'components/material-symbol/icons/Check'
import LinkIcon from 'components/material-symbol/icons/Link'
import SocialSharePopover from 'components/social-share-popover'
import AppStoreBadge from 'images/store-badges/app-store-badge.svg'
import GooglePlayBadge from 'images/store-badges/google-play-badge.png'
import { CARD_BORDER, CARD_SHADOW, MUTED_TEXT, PAGE_BACKGROUND } from './styles'

const DOWNLOAD_SUBTITLE =
  'Connect with your coach and stay up to date on your PGA HOPE program.'

const useStyles = makeStyles(theme => ({
  page: {
    backgroundColor: PAGE_BACKGROUND,
    // The nested theme's body typography still resolves to the base theme's
    // Inter, so set the refresh font here and let plain text inherit it.
    fontFamily: theme.typography.fontFamily,
    padding: theme.spacing(6, 2, 8),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(3, 2, 6),
    },
  },
  card: {
    boxSizing: 'border-box',
    maxWidth: 480,
    margin: '0 auto',
    padding: theme.spacing(5, 4),
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: 24,
    boxShadow: CARD_SHADOW,
    textAlign: 'center',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(4, 2.5),
      borderRadius: 20,
    },
  },
  logo: {
    display: 'block',
    width: 120,
    height: 'auto',
    margin: theme.spacing(0, 'auto', 3),
  },
  iconCircle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 48,
    height: 48,
    margin: theme.spacing(0, 'auto', 2.5),
    borderRadius: '50%',
    // Same navy tint as the other success icons (payment-request prepaid
    // success, PGA Coach+ checkout success).
    backgroundColor: 'rgba(0, 35, 75, 0.08)',
    color: theme.palette.primary.main,
    '& svg': { fontSize: 24 },
  },
  heading: {
    fontSize: 28,
    lineHeight: 1.2,
    [theme.breakpoints.down('xs')]: {
      fontSize: 24,
    },
  },
  eventTitle: {
    margin: theme.spacing(1.5, 0, 2),
    fontSize: 22,
    lineHeight: 1.25,
    [theme.breakpoints.down('xs')]: {
      fontSize: 20,
    },
  },
  body: {
    margin: 0,
    color: MUTED_TEXT,
    fontSize: 15,
    lineHeight: 1.6,
  },
  email: {
    color: theme.palette.text.primary,
    fontWeight: 600,
    overflowWrap: 'anywhere',
  },
  closing: {
    marginTop: theme.spacing(2),
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(3.5),
  },
  download: {
    maxWidth: 480,
    margin: theme.spacing(6, 'auto', 0),
    textAlign: 'center',
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(4),
    },
  },
  downloadTitle: {
    marginBottom: theme.spacing(1),
    fontSize: 22,
    lineHeight: 1.25,
  },
  badges: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  badge: {
    display: 'block',
    height: 48,
    [theme.breakpoints.down('xs')]: {
      height: 44,
    },
  },
}))

const RedesignedConfirmation = ({
  groupEvent,
  contactEmail,
  groupEventLink,
  logo,
}) => {
  const classes = useStyles()

  return (
    <div className={classes.page}>
      <section className={classes.card}>
        {logo && <img src={logo.src} alt={logo.alt} className={classes.logo} />}
        <div className={classes.iconCircle}>
          <CheckIcon aria-hidden="true" />
        </div>
        <Typography variant="h4" component="h1" className={classes.heading}>
          You&apos;re registered!
        </Typography>
        <Typography variant="h5" component="h2" className={classes.eventTitle}>
          {groupEvent.title}
        </Typography>
        {contactEmail && (
          <p className={classes.body}>
            You'll receive an email confirmation shortly to:
            <br />
            <span className={classes.email}>{contactEmail}</span>.
          </p>
        )}
        <p className={`${classes.body} ${classes.closing}`}>
          We look forward to being a part of your golf journey!
        </p>
        <div className={classes.actions}>
          <Button variant="outlined" color="primary" href={groupEventLink}>
            Event Details
          </Button>
          <SocialSharePopover
            variant="menu"
            trigger={
              <Button
                variant="contained"
                color="primary"
                startIcon={<LinkIcon aria-hidden="true" />}
              >
                Share
              </Button>
            }
            url={groupEventLink}
            text={`I just registered for ${groupEvent.title}${
              groupEvent.coachFacility?.name
                ? ` at ${groupEvent.coachFacility.name}`
                : ''
            }!`}
          />
        </div>
      </section>

      <section className={classes.download}>
        <Typography
          variant="h5"
          component="h2"
          className={classes.downloadTitle}
        >
          Download MyPGA Today
        </Typography>
        <p className={classes.body}>{DOWNLOAD_SUBTITLE}</p>
        <div className={classes.badges}>
          <a href={MY_PGA_APPLE_APP_STORE} target="_blank" rel="noreferrer">
            <img
              src={AppStoreBadge}
              alt="MyPGA app on App Store"
              className={classes.badge}
            />
          </a>
          <a href={MY_PGA_GOOGLE_PLAY_STORE} target="_blank" rel="noreferrer">
            <img
              src={GooglePlayBadge}
              alt="MyPGA app on Google Play"
              className={classes.badge}
            />
          </a>
        </div>
      </section>
    </div>
  )
}

RedesignedConfirmation.propTypes = {
  groupEvent: PropTypes.shape({
    title: PropTypes.string,
    coachFacility: PropTypes.shape({ name: PropTypes.string }),
  }).isRequired,
  contactEmail: PropTypes.string,
  groupEventLink: PropTypes.string.isRequired,
  // Optional program mark shown above the success icon (PGA HOPE).
  logo: PropTypes.shape({
    src: PropTypes.string.isRequired,
    alt: PropTypes.string.isRequired,
  }),
}

RedesignedConfirmation.defaultProps = {
  contactEmail: '',
  logo: null,
}

export default RedesignedConfirmation
